import React from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { propTypes } from '../../util/types';
import { sendVerificationEmail, hasCurrentUserErrors } from '../../ducks/user.duck';
import {
  logout,
  authenticationInProgress,
  login,
  signup,
  checkUsername,
} from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { fetchCategoryCounts } from '../../ducks/browse.duck';
import { Topbar } from '../../components';
import { useMyContextFunctions } from '../../context/ContextFunctions';
import { useMyContext } from '../../context/StateHolder';
import LoginForm from '../AuthenticationPage/LoginForm/LoginForm';
import SignupForm from '../AuthenticationPage/SignupForm/SignupForm';
import AuthModal from './AuthModal/AuthModal';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isSignupEmailTakenError, isSignupUsernameTakenError } from '../../util/errors';
import { LOGIN, SIGNUP } from '../../util/enums';

import css from './TopbarContainer.module.css';

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    submitLogin,
    submitSignup,
    onCheckUsername,
    loginError,
    signupError,
    confirmError,
    ...rest
  } = props;

  const { onOpenAuthModal, onSetAuthType } = useMyContextFunctions();
  const { openAuthModal, authType } = useMyContext();
  const intl = useIntl();

  const handleSubmitSignup = async values => {
    const { fname, lname, username, ...rest } = values;

    // Check username availability before submitting. A check that couldn't be made (it
    // rejects) or that a newer one replaced (null) doesn't stop the signup, as with the
    // form's own check while typing.
    if (username && username.trim()) {
      const isAvailable = await onCheckUsername(username.trim()).catch(() => null);
      if (isAvailable === false) {
        return { username: intl.formatMessage({ id: 'SignupForm.usernameTaken' }) };
      }
    }

    const params = {
      firstName: fname.trim(),
      lastName: lname.trim(),
      publicData: {
        username: username.trim(),
      },
      ...rest,
    };
    onSetAuthType('signup');
    await submitSignup(params);
  };

  const handleSubmitLogin = async values => {
    onSetAuthType('login');
    await submitLogin(values);
  };

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : isSignupUsernameTakenError(signupError) ? (
        <FormattedMessage id="SignupForm.usernameTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  // eslint-disable-next-line no-confusing-arrow
  const errorMessage = (error, message) => (error ? message : null);
  const loginOrSignupError =
    authType === 'login'
      ? errorMessage(loginError, loginErrorMessage)
      : errorMessage(signupError, signupErrorMessage);

  const authModal = (
    <AuthModal
      id="AuthenticationModal"
      isOpen={openAuthModal}
      onClose={() => onOpenAuthModal(false)}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      isSignupModal={authType === SIGNUP}
    >
      {authType === LOGIN ? (
        <LoginForm
          className={css.loginForm}
          onSubmit={handleSubmitLogin}
          onSetAuthType={onSetAuthType}
          inProgress={authInProgress}
          isModal
          renderLoginOrSignupError={loginOrSignupError}
        />
      ) : (
        <SignupForm
          className={css.signupForm}
          contentClassName={css.signupFormContent}
          onSubmit={handleSubmitSignup}
          onSetAuthType={onSetAuthType}
          onCheckUsername={onCheckUsername}
          inProgress={authInProgress}
          isModal
          renderLoginOrSignupError={loginOrSignupError}
        />
      )}
    </AuthModal>
  );

  return (
    <>
      <Topbar
        authInProgress={authInProgress}
        currentPage={currentPage}
        currentSearchParams={currentSearchParams}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        history={history}
        isAuthenticated={isAuthenticated}
        authScopes={authScopes}
        location={location}
        notificationCount={notificationCount}
        onLogout={onLogout}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
        showGenericError={hasGenericError}
        {...rest}
      />
      {authModal}
    </>
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const {
    isAuthenticated,
    loginError,
    signupError,
    logoutError,
    confirmError,
    authScopes,
  } = state.auth;

  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount,
    currentUserCustomerNotificationCount,
    currentUserCartCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;

  // Calculate total notification count (customer + provider)
  const notificationCount =
    (currentUserNotificationCount || 0) + (currentUserCustomerNotificationCount || 0);

  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    currentUserCartCount,
    isAuthenticated,
    authScopes,
    loginError,
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  onCheckUsername: username => dispatch(checkUsername(username)),
  onOpenBrowseMenu: () => dispatch(fetchCategoryCounts()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
